import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ritualSteps } from '../data/ritual';
import { images } from '../data/images';
export function Ritual() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [distance, setDistance] = useState(0);
  const [viewportH, setViewportH] = useState(800);
  useLayoutEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      setDistance(Math.max(0, trackRef.current.scrollWidth - window.innerWidth));
      setViewportH(window.innerHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end']
  });
  const x = useTransform(scrollYProgress, [0, 1], [0, -distance]);
  const progress = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  return <section id="ritueel" className="bg-ink text-sand" data-id="element-94" data-mp="20yfchu8oir9h">
      {/* Desktop: horizontale scroll-track */}
      <div ref={sectionRef} className="relative hidden lg:block" style={{
      height: distance + viewportH
    }} data-id="element-95" data-mp="1vzcegtr49lb0">
        <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden" data-id="element-96" data-mp="2awbkid6bl63d">
          <motion.div ref={trackRef} style={{
          x
        }} className="flex w-max items-stretch gap-6 pl-10 pr-10" data-id="element-97" data-mp="25vnwm0de15dd">
            <div className="flex w-[30rem] shrink-0 flex-col justify-between pr-8" data-id="element-98" data-mp="2junk34p5ze7y">
              <h2 className="font-display text-7xl font-medium leading-[0.9]" data-id="element-99" data-mp="2ew3zp8q7yahp">
                Het douche&shy;<span className="italic text-clay" data-id="element-100" data-mp="2tl014mxrsp9c">ritueel</span>
              </h2>
              <p className="max-w-sm leading-relaxed text-sand/70" data-id="element-101" data-mp="2omf3mv9wkjol">
                Negen stappen, geïnspireerd op de hammam. Elke stap heeft een doel en een product dat precies dat doet. Scroll om
                het ritueel te volgen.
              </p>
            </div>
            <img src={images.ritual} alt="Een vrouw brengt rozenwater aan als verfrissende mist" className="h-[32rem] w-[24rem] shrink-0 rounded-[2rem] object-cover" data-id="element-102" data-mp="35ochf9lslo88" />
            {ritualSteps.map((step, i) => <StepCard key={step.title} index={i} title={step.title} text={step.text} products={step.products} data-id="element-103" data-mp="3fkxkhpe94p9f" data-mpi={i} />)}
          </motion.div>
          <div className="mx-10 mt-12 h-px bg-sand/15" data-id="element-104" data-mp="3ofc05t2p21t2">
            <motion.div style={{
            width: progress
          }} className="h-px bg-clay" data-id="element-105" data-mp="3oekcr5j93adv" />
          </div>
        </div>
      </div>

      {/* Mobiel / tablet: verticale lijst */}
      <div className="px-6 py-24 lg:hidden" data-id="element-106" data-mp="28nkwurkvpm5">
        <h2 className="font-display text-5xl font-medium leading-[0.95] sm:text-6xl" data-id="element-107" data-mp="25rlf2030svl">
          Het douche&shy;<span className="italic text-clay" data-id="element-108" data-mp="74ckuz7if8iz">ritueel</span>
        </h2>
        <p className="mt-6 max-w-sm leading-relaxed text-sand/70" data-id="element-109" data-mp="240ju3xq9wndr">
          Negen stappen, geïnspireerd op de hammam. Elke stap heeft een doel en een product dat precies dat doet.
        </p>
        <img src={images.ritual} alt="Een vrouw brengt rozenwater aan als verfrissende mist" className="mt-10 aspect-[4/5] w-full rounded-[2rem] object-cover sm:aspect-[16/10]" data-id="element-110" data-mp="q4ignm3o4rss" />
        <ol className="mt-10 grid gap-4 sm:grid-cols-2" data-id="element-111" data-mp="havrwfxvoot3">
          {ritualSteps.map((step, i) => <li key={step.title} className="flex" data-id="element-112" data-mp="c9g3cdwhs05y" data-mpi={i}>
              <StepCard index={i} title={step.title} text={step.text} products={step.products} fluid data-id="element-113" data-mp="19239135n68i5" />
            </li>)}
        </ol>
      </div>
    </section>;
}
type StepCardProps = {
  index: number;
  title: string;
  text: string;
  products: string[];
  fluid?: boolean;
};
function StepCard({
  index,
  title,
  text,
  products,
  fluid
}: StepCardProps) {
  return <article className={`flex flex-col rounded-[2rem] border border-sand/15 bg-sand/[0.04] p-8 ${fluid ? 'w-full' : 'h-[32rem] w-[20rem] shrink-0'}`} data-id="element-114" data-mp="3krcg3uikk106">
      <span className="font-display text-8xl leading-none text-clay" aria-hidden="true" data-id="element-115" data-mp="3fucafkpmt09l">
        {index + 1}
      </span>
      <h3 className="mt-auto pt-10 font-display text-4xl leading-tight" data-id="element-116" data-mp="1ca1ghc0wwrg6">{title}</h3>
      <p className="mt-2 text-sand/70" data-id="element-117" data-mp="1cc4ikufcab19">{text}</p>
      <div className="mt-6 flex min-h-[2rem] flex-wrap gap-2" data-id="element-118" data-mp="yjxzazgw3nxl">
        {products.map(p => <span key={p} className="whitespace-nowrap rounded-full border border-sand/20 px-3 py-1 text-xs text-sand/80" data-id="element-119" data-mp="13j14cjwvdu5k">
            {p}
          </span>)}
      </div>
    </article>;
}